// src/components/discover/random-character-card.tsx
"use client";

import type { FC } from 'react';
import Link from 'next/link';
import type { Character } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { Dices } from 'lucide-react';
import { useI18n } from '@/context/i18n-context';
import { cn } from '@/lib/utils';
import { memo } from 'react';

interface RandomCharacterCardProps {
  characters: Character[];
}

const RandomCharacterCard: FC<RandomCharacterCardProps> = ({ characters }) => {
  const { t } = useI18n();

  const getRandomCharacterUrl = () => {
    if (characters.length > 0) {
      const publicCharacters = characters.filter(c => c.visibility === 'public');
      if (publicCharacters.length > 0) {
        const randomIndex = Math.floor(Math.random() * publicCharacters.length);
        const randomCharacter = publicCharacters[randomIndex];
        if (randomCharacter) {
          return `/chat/${randomCharacter.username || randomCharacter.id}`;
        }
      }
    }
    return '/'; // Fallback URL
  };

  const randomUrl = getRandomCharacterUrl();

  return (
    <Link
      href={randomUrl}
      className="block h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-2xl"
      aria-label={t('randomCharacterCardAriaLabel') || "Select a random character"}
    >
      <Card
        className={cn(
          "h-full flex flex-col items-center justify-center p-4 text-center text-white transition-all duration-300 ease-in-out group rounded-2xl shadow-lg hover:shadow-primary/40",
          "bg-gradient-to-br from-primary to-accent"
        )}
      >
        <Dices className="h-16 w-16 mb-4 text-primary-foreground/80 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
        <h3 className="text-base font-bold text-white drop-shadow-lg">
          {t('randomBotTitle') || "Random Bot"}
        </h3>
        <p className="text-xs text-white/70 mt-1">
          {t('randomBotDesc') || "Chat with someone new"}
        </p>
      </Card>
    </Link>
  );
};

export default memo(RandomCharacterCard);